<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
      <h5 class="fw-bold text-black mb-0">Historique des affectations</h5>
      <div class="d-flex align-items-center">
        <form class="search-bg svg-color pt-3" @submit.prevent="searchAffectations">
          <input
            type="text"
            v-model="searchTerm"
            @keyup="searchAffectations"
            class="form-control shadow-none text-black"
            placeholder="Rechercher..."
          />
          <button type="submit" class="bg-transparent text-primary transition p-0 border-0">
            <i class="flaticon-search-interface-symbol"></i>
          </button>
        </form>
      </div>
    </div>

    <div class="card-body p-15 p-sm-20 p-md-25">
      <div class="table-responsive">
        <table v-if="affectations.length > 0" class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th>Utilisateur</th>
              <th>Caisse</th>
              <th>Date d'affectation</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(affectation, index) in affectations" :key="index">
              <td>{{ affectation.user ? `${affectation.user.nom} ${affectation.user.prenom}` : 'N/A' }}</td>
              <td>{{ affectation.caisse?.nom || 'N/A' }}</td>
              <td>{{ formatDate(affectation.dateAffectation) }}</td>
              <td>
                <span :class="affectation.statut === 'ACTIF' ? 'badge bg-success' : 'badge bg-secondary'">
                  {{ affectation.statut }}
                </span>
              </td>
              <td>
                <button
                  v-if="affectation.statut === 'INACTIF'"
                  class="btn btn-sm btn-warning"
                  data-bs-toggle="modal"
                  data-bs-target="#AffectationCaisseModal"
                  @click="handleReaffecter(affectation)"
                >
                  <i class="flaticon-shield me-1"></i> Réaffecter
                </button>
                <span v-else class="text-muted">—</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="text-center text-muted py-20">
          Aucune affectation trouvée
        </div>
      </div>

      <div class="pagination-area d-md-flex mt-15 justify-content-between align-items-center">
        <PaginationComponent
          :page="currentPage"
          :totalPages="totalPages"
          :totalElements="totalElements"
          :limit="limit"
          @paginate="handlePaginate"
        />
      </div>
    </div>
  </div>

  <AffectationCaisseModal
    :selectedAffectation="selectedAffectation"
    @affectee="recharger"
  />
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import ApiService from '@/services/ApiService';
import AffectationCaisseModal from './AffectationCaisseModal.vue';
import PaginationComponent from '@/components/Utilities/Pagination.vue';
import { Affectationn } from '@/models/Affectationn';
import { error } from '@/utils/utils';
import { useRoute } from 'vue-router';

export default defineComponent({
  name: 'HistoriqueAffectations',
  components: {
    AffectationCaisseModal,
    PaginationComponent,
  },
  setup() {
    const affectations = ref<Affectationn[]>([]);
    const selectedAffectation = ref<Affectationn | null>(null);
    const searchTerm = ref('');
    const currentPage = ref(1);
    const totalPages = ref(1);
    const totalElements = ref(0);
    const limit = ref(10);
    const route = useRoute();
    const caisseId = route.params.caisseId;

    const formatDate = (date: string) => {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    };

    const getAllAffectations = (page = 1, limi = 10, search = '') => {
      return ApiService.get(`all/affectationsCaisse?page=${page}&limit=${limi}&caisseId=${caisseId}&mot=${search}`)
        .then(({ data }) => {
          affectations.value = data.data.data || [];
          totalPages.value = data.data.totalPages;
          totalElements.value = data.data.totalElements;
          limit.value = data.data.limit;
        })
        .catch(({ response }) => {
          error(response?.data?.message || 'Erreur lors de la récupération');
        });
    };

    const handleReaffecter = (affectation: Affectationn) => {
      selectedAffectation.value = affectation;
    };

    const recharger = () => {
      getAllAffectations(currentPage.value, limit.value, searchTerm.value);
    };

    const searchAffectations = () => {
      getAllAffectations(1, limit.value, searchTerm.value);
    };

    const handlePaginate = ({ page_, limit_ }) => {
      currentPage.value = page_;
      limit.value = limit_;
      getAllAffectations(page_, limit_, searchTerm.value);
    };

    onMounted(() => getAllAffectations());

    return {
      affectations,
      selectedAffectation,
      searchTerm,
      currentPage,
      totalPages,
      totalElements,
      limit,
      formatDate,
      getAllAffectations,
      handleReaffecter,
      recharger,
      searchAffectations,
      handlePaginate,
    };
  },
});
</script>